import { useEffect } from 'react';
import './Lightbox.css';

export interface LightboxItem {
  type: 'image' | 'video';
  url: string;
  caption?: string;
}

export interface LightboxProps {
  item: LightboxItem;
  onClose: () => void;
  onNext?: () => void;
  onPrevious?: () => void;
}

/**
 * Generic, feature-agnostic lightbox overlay — reusable anywhere media
 * needs a full-screen focused view (currently: Gallery; potentially: Project
 * detail galleries, future Media Library preview in Admin). Deliberately
 * has no knowledge of albums/projects/entities, keeping it in packages/ui
 * rather than feature-local.
 */
export function Lightbox({ item, onClose, onNext, onPrevious }: LightboxProps) {
  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose();
      if (event.key === 'ArrowRight' && onNext) onNext();
      if (event.key === 'ArrowLeft' && onPrevious) onPrevious();
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose, onNext, onPrevious]);

  return (
    <div
      className="dma-lightbox"
      role="dialog"
      aria-modal="true"
      aria-label={item.caption ?? 'نمایش تصویر'}
      onClick={onClose}
    >
      <button
        type="button"
        className="dma-lightbox__close"
        onClick={onClose}
        aria-label="بستن"
      >
        ×
      </button>

      {onPrevious ? (
        <button
          type="button"
          className="dma-lightbox__nav dma-lightbox__nav--prev"
          aria-label="قبلی"
          onClick={(event) => {
            event.stopPropagation();
            onPrevious();
          }}
        >
          ‹
        </button>
      ) : null}

      <div className="dma-lightbox__content" onClick={(event) => event.stopPropagation()}>
        {item.type === 'image' ? (
          <img src={item.url} alt={item.caption ?? ''} className="dma-lightbox__media" />
        ) : (
          <video src={item.url} controls className="dma-lightbox__media" />
        )}
        {item.caption ? <p className="dma-lightbox__caption">{item.caption}</p> : null}
      </div>

      {onNext ? (
        <button
          type="button"
          className="dma-lightbox__nav dma-lightbox__nav--next"
          aria-label="بعدی"
          onClick={(event) => {
            event.stopPropagation();
            onNext();
          }}
        >
          ›
        </button>
      ) : null}
    </div>
  );
}
